<template>
  <div>
    <head>
      <meta charset="utf-8" />
      <title>Department Performance Details - AI Analytics</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <link rel="shortcut icon" href="assets/images/favicon.png">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <NavigationBar/>
    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">
                  <i class="bx bx-bar-chart-alt-2 me-2"></i>{{departmentName}}
                </h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a @click="gotTo('dashboard')">Dashboard</a></li>
                    <li class="breadcrumb-item"><a @click="goBack">Performance</a></li>
                    <li class="breadcrumb-item active">Details</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Department Header Card -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card" :class="getHeaderCardClass()">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-md-8">
                      <div class="d-flex align-items-center">
                        <div class="avatar-lg me-4">
                          <span class="avatar-title rounded-circle bg-white">
                            <i :class="[getDepartmentIcon(), 'font-size-24', getIconColorClass()]"></i>
                          </span>
                        </div>
                        <div class="text-white">
                          <h3 class="text-white mb-2">{{departmentName}}</h3>
                          <p class="mb-0 text-white-50">
                            <strong>Financial Year:</strong> {{formatDate(financialYearStart)}} to {{formatDate(financialYearEnd)}}
                          </p>
                          <div class="mt-2" v-if="aiSummary">
                            <span class="badge badge-lg" :class="'bg-' + getStatusColor(aiSummary.overallStatus)">
                              {{aiSummary.overallStatus}}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4 text-md-end mt-3 mt-md-0">
                      <button @click="goBack" class="btn btn-light me-2">
                        <i class="bx bx-arrow-back me-1"></i> Back
                      </button>
                      <button @click="refreshData" class="btn btn-outline-light me-2" :disabled="loading">
                        <i class="bx bx-refresh" :class="{'bx-spin': loading}"></i>
                      </button>
                      <button @click="exportReport" class="btn btn-success">
                        <i class="bx bx-download me-1"></i> Export
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Performance Summary Cards -->
          <div class="row">
            <div class="col-xl-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">Target</p>
                      <h4 class="mb-0">{{formatPrice(summary.totalTarget)}}</h4>
                    </div>
                    <div class="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                      <span class="avatar-title">
                        <i class="bx bx-target-lock font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">Collected</p>
                      <h4 class="mb-0">{{formatPrice(summary.totalCollected)}}</h4>
                      <p class="mb-0 mt-2" :class="getPerformanceColor(summary.performance)">
                        <i class="bx" :class="summary.performance >= 75 ? 'bx-trending-up' : 'bx-trending-down'"></i>
                        <strong>{{summary.performance}}%</strong>
                      </p>
                    </div>
                    <div class="avatar-sm rounded-circle align-self-center mini-stat-icon" :class="getPerformanceBg(summary.performance)">
                      <span class="avatar-title" :class="getPerformanceBg(summary.performance)">
                        <i class="bx bx-money font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">Variance</p>
                      <h4 class="mb-0" :class="summary.variance >= 0 ? 'text-danger' : 'text-success'">
                        {{formatPrice(Math.abs(summary.variance))}}
                      </h4>
                      <p class="mb-0 mt-2" :class="summary.variance >= 0 ? 'text-danger' : 'text-success'">
                        <i class="bx" :class="summary.variance >= 0 ? 'bx-down-arrow-alt' : 'bx-up-arrow-alt'"></i>
                        {{summary.variance >= 0 ? 'Below' : 'Above'}} target
                      </p>
                    </div>
                    <div class="avatar-sm rounded-circle align-self-center mini-stat-icon" :class="summary.variance >= 0 ? 'bg-danger' : 'bg-success'">
                      <span class="avatar-title" :class="summary.variance >= 0 ? 'bg-danger' : 'bg-success'">
                        <i class="bx font-size-24" :class="summary.variance >= 0 ? 'bx-trending-down' : 'bx-trending-up'"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">Income Streams</p>
                      <h4 class="mb-0">{{incomeStreams.length}}</h4>
                      <p class="mb-0 mt-2 text-muted">
                        <small>Active streams</small>
                      </p>
                    </div>
                    <div class="avatar-sm rounded-circle bg-info align-self-center mini-stat-icon">
                      <span class="avatar-title">
                        <i class="bx bx-list-ul font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- AI Summary Card -->
          <div class="row mb-4" v-if="aiSummary">
            <div class="col-12">
              <div class="card border-primary">
                <div class="card-header bg-primary text-white">
                  <h5 class="mb-0 text-white">
                    <i class="bx bx-brain me-2"></i>AI Performance Summary
                  </h5>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-8">
                      <div class="alert" :class="'alert-' + getStatusColor(aiSummary.overallStatus)" role="alert">
                        <h5 class="alert-heading">
                          <i class="bx bx-info-circle me-2"></i>Key Insight
                        </h5>
                        <p class="mb-0">{{aiSummary.keyInsight}}</p>
                      </div>

                      <div class="alert alert-warning" role="alert">
                        <h6 class="alert-heading">
                          <i class="bx bx-error-circle me-2"></i>Priority Action
                        </h6>
                        <p class="mb-0">{{aiSummary.priorityAction}}</p>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="text-center p-3 bg-light rounded">
                        <h6 class="text-muted mb-3">Stream Performance</h6>
                        <div class="mb-3">
                          <h3 class="text-success mb-0">{{aiSummary.topPerformingStreams}}</h3>
                          <small class="text-muted">Top Performing</small>
                        </div>
                        <div>
                          <h3 class="text-danger mb-0">{{aiSummary.underperformingStreams}}</h3>
                          <small class="text-muted">Needs Attention</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Trend Analysis with AI -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white">
                  <div class="row align-items-center">
                    <div class="col-md-6">
                      <h4 class="card-title mb-0">
                        <i class="bx bx-line-chart me-2"></i>Collection Trends with AI Analysis
                      </h4>
                    </div>
                    <div class="col-md-6 text-md-end">
                      <div class="btn-group" role="group">
                        <button
                            @click="activeTrendTab = 'weekly'"
                            class="btn btn-sm"
                            :class="activeTrendTab === 'weekly' ? 'btn-primary' : 'btn-outline-primary'">
                          <i class="bx bx-calendar-week me-1"></i>Weekly
                        </button>
                        <button
                            @click="activeTrendTab = 'monthly'"
                            class="btn btn-sm"
                            :class="activeTrendTab === 'monthly' ? 'btn-primary' : 'btn-outline-primary'">
                          <i class="bx bx-calendar me-1"></i>Monthly
                        </button>
                        <button
                            @click="activeTrendTab = 'yearly'"
                            class="btn btn-sm"
                            :class="activeTrendTab === 'yearly' ? 'btn-primary' : 'btn-outline-primary'">
                          <i class="bx bx-calendar-star me-1"></i>Yearly
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="card-body">

                  <!-- Weekly Trend -->
                  <div v-show="activeTrendTab === 'weekly'">
                    <div class="row mb-3">
                      <div class="col-md-9">
                        <div id="weeklyChart" style="width: 100%; height: 400px;"></div>
                      </div>
                      <div class="col-md-3">
                        <div class="card bg-light border-0" v-if="trendAI.weekly">
                          <div class="card-body">
                            <h6 class="text-primary mb-3">
                              <i class="bx bx-brain me-1"></i>AI Insights
                            </h6>
                            <div class="mb-3">
                              <small class="text-muted d-block mb-1">Trend</small>
                              <span class="badge" :class="getTrendBadgeClass(trendAI.weekly.trend)">
                                <i class="bx" :class="getTrendIcon(trendAI.weekly.trend)"></i>
                                {{trendAI.weekly.trend}}
                              </span>
                            </div>
                            <div class="mb-3">
                              <small class="text-muted d-block mb-1">Average/Week</small>
                              <strong>{{formatPrice(trendAI.weekly.average)}}</strong>
                            </div>
                            <div class="mb-3">
                              <small class="text-muted d-block mb-1">Best Week</small>
                              <strong class="text-success">{{formatPrice(trendAI.weekly.best)}}</strong>
                            </div>
                            <div class="mb-3">
                              <small class="text-muted d-block mb-1">Consistency</small>
                              <div class="progress" style="height: 20px;">
                                <div class="progress-bar" :class="trendAI.weekly.consistency >= 70 ? 'bg-success' : 'bg-warning'"
                                     :style="{width: trendAI.weekly.consistency + '%'}">
                                  <strong style="font-size: 11px;">{{trendAI.weekly.consistency}}%</strong>
                                </div>
                              </div>
                            </div>
                            <div class="alert alert-info alert-sm mb-0">
                              <small><strong>AI:</strong> {{trendAI.weekly.insight}}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Monthly Trend -->
                  <div v-show="activeTrendTab === 'monthly'">
                    <div class="row mb-3">
                      <div class="col-md-9">
                        <div id="monthlyChart" style="width: 100%; height: 400px;"></div>
                      </div>
                      <div class="col-md-3">
                        <div class="card bg-light border-0" v-if="trendAI.monthly">
                          <div class="card-body">
                            <h6 class="text-primary mb-3">
                              <i class="bx bx-brain me-1"></i>AI Insights
                            </h6>
                            <div class="mb-3">
                              <small class="text-muted d-block mb-1">Trend</small>
                              <span class="badge" :class="getTrendBadgeClass(trendAI.monthly.trend)">
                                <i class="bx" :class="getTrendIcon(trendAI.monthly.trend)"></i>
                                {{trendAI.monthly.trend}}
                              </span>
                            </div>
                            <div class="mb-3">
                              <small class="text-muted d-block mb-1">Average/Month</small>
                              <strong>{{formatPrice(trendAI.monthly.average)}}</strong>
                            </div>
                            <div class="mb-3">
                              <small class="text-muted d-block mb-1">Best Month</small>
                              <strong class="text-success">{{formatPrice(trendAI.monthly.best)}}</strong>
                            </div>
                            <div class="mb-3">
                              <small class="text-muted d-block mb-1">Growth Rate</small>
                              <strong :class="trendAI.monthly.growth >= 0 ? 'text-success' : 'text-danger'">
                                {{trendAI.monthly.growth >= 0 ? '+' : ''}}{{trendAI.monthly.growth}}%
                              </strong>
                            </div>
                            <div class="alert alert-info alert-sm mb-0">
                              <small><strong>AI:</strong> {{trendAI.monthly.insight}}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Yearly Trend -->
                  <div v-show="activeTrendTab === 'yearly'">
                    <div class="row mb-3">
                      <div class="col-md-9">
                        <div id="yearlyChart" style="width: 100%; height: 400px;"></div>
                      </div>
                      <div class="col-md-3">
                        <div class="card bg-light border-0" v-if="trendAI.yearly">
                          <div class="card-body">
                            <h6 class="text-primary mb-3">
                              <i class="bx bx-brain me-1"></i>AI Insights
                            </h6>
                            <div class="mb-3">
                              <small class="text-muted d-block mb-1">Projection</small>
                              <span class="badge" :class="trendAI.yearly.projection >= 75 ? 'bg-success' : 'bg-danger'">
                                {{trendAI.yearly.projectedPerformance}}%
                              </span>
                            </div>
                            <div class="mb-3">
                              <small class="text-muted d-block mb-1">Current Pace</small>
                              <strong>{{formatPrice(trendAI.yearly.currentPace)}}</strong>
                              <small class="d-block text-muted">/day needed</small>
                            </div>
                            <div class="mb-3">
                              <small class="text-muted d-block mb-1">Total Collected</small>
                              <strong>{{formatPrice(trendAI.yearly.totalCollected)}}</strong>
                            </div>
                            <div class="mb-3">
                              <small class="text-muted d-block mb-1">Days Remaining</small>
                              <strong class="text-warning">{{trendAI.yearly.daysRemaining}}</strong>
                            </div>
                            <div class="alert alert-info alert-sm mb-0">
                              <small><strong>AI:</strong> {{trendAI.yearly.insight}}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

          <!-- Income Streams Performance Table -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white">
                  <div class="row align-items-center">
                    <div class="col-md-6">
                      <h4 class="card-title mb-0">
                        <i class="bx bx-list-check me-2"></i>Income Streams Performance with AI
                      </h4>
                    </div>
                    <div class="col-md-6 text-md-end">
                      <div class="btn-group" role="group">
                        <button
                            @click="streamSortBy = 'performance'"
                            class="btn btn-sm"
                            :class="streamSortBy === 'performance' ? 'btn-primary' : 'btn-outline-primary'">
                          <i class="bx bx-sort-down me-1"></i>Performance
                        </button>
                        <button
                            @click="streamSortBy = 'collected'"
                            class="btn btn-sm"
                            :class="streamSortBy === 'collected' ? 'btn-primary' : 'btn-outline-primary'">
                          <i class="bx bx-money me-1"></i>Amount
                        </button>
                        <button
                            @click="streamSortBy = 'name'"
                            class="btn btn-sm"
                            :class="streamSortBy === 'name' ? 'btn-primary' : 'btn-outline-primary'">
                          <i class="bx bx-sort-a-z me-1"></i>Name
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card-body">
                  <!-- Loading State -->
                  <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;"></div>
                    <h5 class="text-muted">Loading income streams with AI analysis...</h5>
                  </div>

                  <!-- Data Table -->
                  <div v-else-if="incomeStreams.length > 0" class="table-responsive">
                    <table class="table table-hover align-middle">
                      <thead class="table-light">
                      <tr>
                        <th style="width: 5%">#</th>
                        <th style="width: 25%">Income Type</th>
                        <th class="text-end" style="width: 12%">Target</th>
                        <th class="text-end" style="width: 12%">Collected</th>
                        <th class="text-center" style="width: 8%">Transactions</th>
                        <th class="text-center" style="width: 12%">Performance</th>
                        <th class="text-center" style="width: 10%">Status</th>
                        <th style="width: 16%">AI Recommendation</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(stream, index) in sortedIncomeStreams" :key="stream.incomeTypeId"
                          :class="getStreamRowClass(stream.streamStatus)">
                        <td>{{index + 1}}</td>
                        <td>
                          <div>
                            <h6 class="mb-1">{{stream.incomeTypeDescription}}</h6>
                            <small class="text-muted">
                              <span class="badge bg-soft-secondary text-secondary">{{stream.incomeTypePrefix}}</span>
                            </small>
                          </div>
                        </td>
                        <td class="text-end">
                          <strong>{{formatPrice(stream.target)}}</strong>
                        </td>
                        <td class="text-end">
                          <strong :class="getPerformanceColor(stream.performance)">
                            {{formatPrice(stream.collected)}}
                          </strong>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-soft-info text-info">
                            {{stream.transactions}}
                          </span>
                        </td>
                        <td>
                          <div class="text-center">
                            <div class="progress mb-2" style="height: 18px;">
                              <div
                                  class="progress-bar"
                                  :class="getProgressBarClass(stream.performance)"
                                  :style="{ width: Math.min(stream.performance, 100) + '%' }">
                                <strong style="font-size: 11px;">{{stream.performance}}%</strong>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td class="text-center">
                          <span class="badge" :class="getStreamStatusBadgeClass(stream.streamStatus)">
                            {{stream.streamStatus}}
                          </span>
                        </td>
                        <td>
                          <small class="text-muted">{{stream.aiRecommendation}}</small>
                        </td>
                      </tr>
                      </tbody>
                      <tfoot class="table-dark">
                      <tr>
                        <th colspan="2" class="text-end">TOTALS:</th>
                        <th class="text-end">{{formatPrice(summary.totalTarget)}}</th>
                        <th class="text-end">{{formatPrice(summary.totalCollected)}}</th>
                        <th class="text-center">{{summary.totalTransactions}}</th>
                        <th class="text-center">
                          <strong>{{summary.performance}}%</strong>
                        </th>
                        <th colspan="2"></th>
                      </tr>
                      </tfoot>
                    </table>
                  </div>

                  <!-- Empty State -->
                  <div v-else class="text-center py-5">
                    <i class="bx bx-error-circle display-1 text-muted"></i>
                    <h5 class="mt-3">No Income Streams Found</h5>
                    <p class="text-muted">No income streams are configured for this department.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">
              ©{{new Date().getFullYear()}} County Revenue System
            </div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                AI-Powered Department Performance Details
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import router from "@/router";
import {biller, execute2} from "@/api";
import moment from 'moment';
import Highcharts from 'highcharts';

// Set default global options for Highcharts
Highcharts.setOptions({
  lang: {
    thousandsSep: ',',
    decimalPoint: '.'
  }
});

export default {
  name: "DepartmentPerformanceDetails",
  components: {NavigationBar},

  data() {
    return {
      loading: false,
      departmentID: '',
      departmentName: '',
      financialYearStart: '',
      financialYearEnd: '',
      incomeStreams: [],
      summary: {
        totalTarget: 0,
        totalCollected: 0,
        totalTransactions: 0,
        variance: 0,
        performance: 0
      },
      aiSummary: null,
      streamSortBy: 'performance',

      // Trend data
      weeklyTrend: [],
      monthlyTrend: [],
      yearlyTrend: [],
      trendAI: {
        weekly: null,
        monthly: null,
        yearly: null
      },

      // Active tab
      activeTrendTab: 'weekly'
    }
  },

  computed: {
    sortedIncomeStreams() {
      if (!this.incomeStreams || this.incomeStreams.length === 0) return [];

      const sorted = [...this.incomeStreams];

      if (this.streamSortBy === 'performance') {
        return sorted.sort((a, b) => a.performance - b.performance);
      } else if (this.streamSortBy === 'collected') {
        return sorted.sort((a, b) => b.collected - a.collected);
      } else if (this.streamSortBy === 'name') {
        return sorted.sort((a, b) => a.incomeTypeDescription.localeCompare(b.incomeTypeDescription));
      }

      return sorted;
    }
  },

  mounted() {
    this.loadSessionData();
    this.getDepartmentDetails();
    this.menuAccess();
  },

  watch: {
    activeTrendTab(newTab) {
      // Recreate chart when tab changes
      this.$nextTick(() => {
        if (newTab === 'weekly' && this.weeklyTrend.length > 0) {
          this.createWeeklyChart();
        } else if (newTab === 'monthly' && this.monthlyTrend.length > 0) {
          this.createMonthlyChart();
        } else if (newTab === 'yearly' && this.yearlyTrend.length > 0) {
          this.createYearlyChart();
        }
      });
    }
  },

  methods: {
    menuAccess() {
      const data = new FormData();
      data.append("function", "menuAccess");
      data.append("menu", "Department Performance Details");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute2(data, biller).then(() => {}).catch(() => {});
    },

    loadSessionData() {
      this.departmentID = sessionStorage.getItem("selectedDepartmentID") || '';
      this.departmentName = sessionStorage.getItem("selectedDepartmentName") || 'Department';
      this.financialYearStart = sessionStorage.getItem("financialYearStart") || '';
      this.financialYearEnd = sessionStorage.getItem("financialYearEnd") || '';

      if (!this.departmentID) {
        this.$swal("Error", "No department selected", "error");
        this.goBack();
      }
    },

    getDepartmentDetails() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getDepartmentPerformanceDetailsAI");
      data.append("departmentID", this.departmentID);

      execute2(data, biller)
          .then((res) => {
            this.loading = false;
            console.log("API Response:", res.data);

            // Handle nested data structure
            const responseData = res.data.data?.data || res.data.data;

            if (res.data.success && responseData) {
              this.incomeStreams = responseData.incomeStreams || [];
              this.summary = responseData.summary || this.summary;
              this.aiSummary = responseData.aiSummary || null;

              // Load trend data after main data is loaded
              this.getTrendData();
            } else {
              this.$swal("Error", res.data.message || "Failed to load department details", "error");
            }
          })
          .catch((e) => {
            this.loading = false;
            console.error("Error:", e);
          });
    },

    getTrendData() {
      // Get weekly trend (last 12 weeks)
      this.getWeeklyTrend();

      // Get monthly trend (last 12 months)
      this.getMonthlyTrend();

      // Get yearly trend (by month for current FY)
      this.getYearlyTrend();
    },

    getWeeklyTrend() {
      const data = new FormData();
      data.append("function", "getDepartmentWeeklyTrend");
      data.append("departmentID", this.departmentID);
      data.append("weeks", 12);

      execute2(data, biller)
          .then((res) => {
            const responseData = res.data.data?.data || res.data.data;
            if (res.data.success && responseData) {
              this.weeklyTrend = responseData.trend || [];
              this.trendAI.weekly = this.analyzeWeeklyTrend(this.weeklyTrend);
              this.$nextTick(() => {
                this.createWeeklyChart();
              });
            }
          })
          .catch((e) => console.error("Error loading weekly trend:", e));
    },

    getMonthlyTrend() {
      const data = new FormData();
      data.append("function", "getDepartmentMonthlyTrend");
      data.append("departmentID", this.departmentID);
      data.append("months", 12);

      execute2(data, biller)
          .then((res) => {
            const responseData = res.data.data?.data || res.data.data;
            if (res.data.success && responseData) {
              this.monthlyTrend = responseData.trend || [];
              this.trendAI.monthly = this.analyzeMonthlyTrend(this.monthlyTrend);
              this.$nextTick(() => {
                this.createMonthlyChart();
              });
            }
          })
          .catch((e) => console.error("Error loading monthly trend:", e));
    },

    getYearlyTrend() {
      const data = new FormData();
      data.append("function", "getDepartmentYearlyTrend");
      data.append("departmentID", this.departmentID);
      data.append("financialYearStart", this.financialYearStart);
      data.append("financialYearEnd", this.financialYearEnd);

      execute2(data, biller)
          .then((res) => {
            const responseData = res.data.data?.data || res.data.data;
            if (res.data.success && responseData) {
              this.yearlyTrend = responseData.trend || [];
              this.trendAI.yearly = this.analyzeYearlyTrend(this.yearlyTrend);
              this.$nextTick(() => {
                this.createYearlyChart();
              });
            }
          })
          .catch((e) => console.error("Error loading yearly trend:", e));
    },

    analyzeWeeklyTrend(data) {
      if (!data || data.length === 0) return null;

      const amounts = data.map(d => d.amount);
      const average = amounts.reduce((a, b) => a + b, 0) / amounts.length;
      const best = Math.max(...amounts);
      const worst = Math.min(...amounts);

      // Calculate consistency (inverse of coefficient of variation)
      const stdDev = Math.sqrt(amounts.reduce((sq, n) => sq + Math.pow(n - average, 2), 0) / amounts.length);
      const consistency = average > 0 ? Math.max(0, 100 - ((stdDev / average) * 100)) : 0;

      // Determine trend
      const firstHalf = amounts.slice(0, Math.floor(amounts.length / 2)).reduce((a, b) => a + b, 0);
      const secondHalf = amounts.slice(Math.floor(amounts.length / 2)).reduce((a, b) => a + b, 0);
      let trend = 'STABLE';
      if (secondHalf > firstHalf * 1.1) trend = 'IMPROVING';
      else if (secondHalf < firstHalf * 0.9) trend = 'DECLINING';

      // Generate insight
      let insight = '';
      if (trend === 'IMPROVING' && consistency >= 70) {
        insight = 'Excellent! Collections are growing consistently.';
      } else if (trend === 'IMPROVING') {
        insight = 'Collections improving but volatile. Focus on consistency.';
      } else if (trend === 'DECLINING') {
        insight = 'Warning! Collections declining. Immediate action needed.';
      } else if (consistency < 50) {
        insight = 'Collections unstable. Implement daily monitoring.';
      } else {
        insight = 'Stable collections. Maintain current strategies.';
      }

      return {
        average: Math.round(average),
        best: Math.round(best),
        worst: Math.round(worst),
        consistency: Math.round(consistency),
        trend,
        insight
      };
    },

    analyzeMonthlyTrend(data) {
      if (!data || data.length === 0) return null;

      const amounts = data.map(d => d.amount);
      const average = amounts.reduce((a, b) => a + b, 0) / amounts.length;
      const best = Math.max(...amounts);

      // Calculate growth rate
      const firstMonth = amounts[0];
      const lastMonth = amounts[amounts.length - 1];
      const growth = firstMonth > 0 ? ((lastMonth - firstMonth) / firstMonth) * 100 : 0;

      // Determine trend
      let trend = 'STABLE';
      if (growth > 10) trend = 'IMPROVING';
      else if (growth < -10) trend = 'DECLINING';

      // Generate insight
      let insight = '';
      if (growth > 20) {
        insight = 'Outstanding! Strong month-over-month growth.';
      } else if (growth > 10) {
        insight = 'Good progress. Keep up the momentum.';
      } else if (growth < -10) {
        insight = 'Declining trend. Review strategies urgently.';
      } else if (growth < 0) {
        insight = 'Slight decline. Monitor and adjust tactics.';
      } else {
        insight = 'Steady performance. Look for growth opportunities.';
      }

      return {
        average: Math.round(average),
        best: Math.round(best),
        growth: Math.round(growth * 10) / 10,
        trend,
        insight
      };
    },

    analyzeYearlyTrend(data) {
      if (!data || data.length === 0) return null;

      const totalCollected = data.reduce((sum, d) => sum + d.amount, 0);
      const target = this.summary.totalTarget || 0;
      const performance = target > 0 ? (totalCollected / target) * 100 : 0;

      // Calculate required daily pace
      const today = new Date();
      const fyEnd = new Date(this.financialYearEnd);
      const daysRemaining = Math.ceil((fyEnd - today) / (1000 * 60 * 60 * 24));
      const remaining = target - totalCollected;
      const currentPace = daysRemaining > 0 ? remaining / daysRemaining : 0;

      // Project final performance based on trend
      const amounts = data.map(d => d.amount);
      const avgMonthly = amounts.reduce((a, b) => a + b, 0) / amounts.length;
      const monthsRemaining = Math.ceil(daysRemaining / 30);
      const projectedTotal = totalCollected + (avgMonthly * monthsRemaining);
      const projectedPerformance = target > 0 ? (projectedTotal / target) * 100 : 0;

      // Generate insight
      let insight = '';
      if (projectedPerformance >= 90) {
        insight = 'On track to meet target! Maintain current pace.';
      } else if (projectedPerformance >= 75) {
        insight = 'Likely to reach 75%+. Push harder for full target.';
      } else if (projectedPerformance >= 50) {
        insight = 'At risk of missing target. Intensify efforts now.';
      } else {
        insight = 'Critical! Major intervention needed immediately.';
      }

      return {
        totalCollected: Math.round(totalCollected),
        projection: Math.round(projectedPerformance),
        projectedPerformance: Math.round(projectedPerformance * 10) / 10,
        currentPace: Math.round(currentPace),
        daysRemaining,
        insight
      };
    },

    createWeeklyChart() {
      if (!this.weeklyTrend || this.weeklyTrend.length === 0) {
        console.log('No weekly trend data available');
        return;
      }

      try {
        const labels = this.weeklyTrend.map(d => d.label);
        const data = this.weeklyTrend.map(d => d.amount);

        console.log('Creating weekly chart with data:', { labels, data });

        Highcharts.chart('weeklyChart', {
          chart: {
            type: 'line',
            style: {
              fontFamily: 'Montserrat, sans-serif'
            }
          },
          title: {
            text: null
          },
          xAxis: {
            categories: labels
          },
          yAxis: {
            title: {
              text: 'Collections (KES)'
            },
            labels: {
              formatter: function() {
                return 'KES ' + (this.value / 1000).toFixed(0) + 'K';
              }
            }
          },
          tooltip: {
            formatter: function() {
              return '<b>' + this.x + '</b><br/>KES ' + Highcharts.numberFormat(this.y, 0, '.', ',');
            }
          },
          plotOptions: {
            line: {
              dataLabels: {
                enabled: false
              },
              enableMouseTracking: true,
              marker: {
                enabled: true,
                radius: 4
              }
            }
          },
          series: [{
            name: 'Weekly Collections',
            data: data,
            color: '#556ee6',
            lineWidth: 3
          }],
          credits: {
            enabled: false
          }
        });
      } catch (error) {
        console.error('Error creating weekly chart:', error);
      }
    },

    createMonthlyChart() {
      if (!this.monthlyTrend || this.monthlyTrend.length === 0) {
        console.log('No monthly trend data available');
        return;
      }

      try {
        const labels = this.monthlyTrend.map(d => d.label);
        const data = this.monthlyTrend.map(d => d.amount);
        const average = data.reduce((a, b) => a + b, 0) / data.length;

        const coloredData = data.map(val => ({
          y: val,
          color: val >= average ? '#34c38f' : '#f1b44c'
        }));

        console.log('Creating monthly chart with data:', { labels, coloredData });

        Highcharts.chart('monthlyChart', {
          chart: {
            type: 'column',
            style: {
              fontFamily: 'Montserrat, sans-serif'
            }
          },
          title: {
            text: null
          },
          xAxis: {
            categories: labels
          },
          yAxis: {
            title: {
              text: 'Collections (KES)'
            },
            labels: {
              formatter: function() {
                return 'KES ' + (this.value / 1000000).toFixed(1) + 'M';
              }
            }
          },
          tooltip: {
            formatter: function() {
              return '<b>' + this.x + '</b><br/>KES ' + Highcharts.numberFormat(this.y, 0, '.', ',');
            }
          },
          plotOptions: {
            column: {
              dataLabels: {
                enabled: false
              }
            }
          },
          series: [{
            name: 'Monthly Collections',
            data: coloredData,
            showInLegend: false
          }],
          credits: {
            enabled: false
          }
        });
      } catch (error) {
        console.error('Error creating monthly chart:', error);
      }
    },

    createYearlyChart() {
      if (!this.yearlyTrend || this.yearlyTrend.length === 0) {
        console.log('No yearly trend data available');
        return;
      }

      try {
        const labels = this.yearlyTrend.map(d => d.label);
        const data = this.yearlyTrend.map(d => d.amount);

        // Calculate cumulative
        const cumulative = [];
        let sum = 0;
        data.forEach(val => {
          sum += val;
          cumulative.push(sum);
        });

        // Calculate target line
        const monthlyTarget = this.summary.totalTarget / 12;
        const targetLine = labels.map((_, idx) => monthlyTarget * (idx + 1));

        console.log('Creating yearly chart with data:', { labels, cumulative, targetLine });

        Highcharts.chart('yearlyChart', {
          chart: {
            type: 'line',
            style: {
              fontFamily: 'Montserrat, sans-serif'
            }
          },
          title: {
            text: null
          },
          xAxis: {
            categories: labels
          },
          yAxis: {
            title: {
              text: 'Cumulative Collections (KES)'
            },
            labels: {
              formatter: function() {
                return 'KES ' + (this.value / 1000000).toFixed(0) + 'M';
              }
            }
          },
          tooltip: {
            shared: true,
            formatter: function() {
              let s = '<b>' + this.x + '</b>';
              this.points.forEach(point => {
                s += '<br/><span style="color:' + point.color + '">\u25CF</span> ' +
                    point.series.name + ': <b>KES ' + Highcharts.numberFormat(point.y, 0, '.', ',') + '</b>';
              });
              return s;
            }
          },
          plotOptions: {
            line: {
              dataLabels: {
                enabled: false
              },
              marker: {
                enabled: true,
                radius: 4
              }
            }
          },
          series: [{
            name: 'Actual Collections',
            data: cumulative,
            color: '#556ee6',
            lineWidth: 3
          }, {
            name: 'Target',
            data: targetLine,
            color: '#f46a6a',
            lineWidth: 2,
            dashStyle: 'Dash',
            marker: {
              enabled: false
            }
          }],
          credits: {
            enabled: false
          }
        });
      } catch (error) {
        console.error('Error creating yearly chart:', error);
      }
    },

    getTrendIcon(trend) {
      if (trend === 'IMPROVING') return 'bx-trending-up';
      if (trend === 'DECLINING') return 'bx-trending-down';
      return 'bx-minus';
    },

    getTrendBadgeClass(trend) {
      if (trend === 'IMPROVING') return 'bg-success';
      if (trend === 'DECLINING') return 'bg-danger';
      return 'bg-secondary';
    },

    refreshData() {
      this.getDepartmentDetails();
    },

    exportReport() {
      const headers = [
        '#', 'Income Type', 'Prefix', 'Target', 'Collected', 'Variance',
        'Performance %', 'Transactions', 'Status', 'AI Recommendation'
      ];

      const rows = this.sortedIncomeStreams.map((stream, index) => [
        index + 1,
        `"${stream.incomeTypeDescription}"`,
        stream.incomeTypePrefix,
        stream.target,
        stream.collected,
        stream.variance,
        stream.performance,
        stream.transactions,
        stream.streamStatus,
        `"${stream.aiRecommendation}"`
      ]);

      const metadata = [
        ['DEPARTMENT PERFORMANCE DETAILED REPORT WITH AI'],
        ['Department:', this.departmentName],
        ['Department ID:', this.departmentID],
        ['Generated:', moment().format('DD/MM/YYYY HH:mm:ss')],
        ['Financial Year:', `${this.formatDate(this.financialYearStart)} to ${this.formatDate(this.financialYearEnd)}`],
        [],
        ['AI SUMMARY'],
        ['Overall Status:', this.aiSummary?.overallStatus || 'N/A'],
        ['Top Performing Streams:', this.aiSummary?.topPerformingStreams || 0],
        ['Underperforming Streams:', this.aiSummary?.underperformingStreams || 0],
        ['Key Insight:', `"${this.aiSummary?.keyInsight || 'N/A'}"`],
        ['Priority Action:', `"${this.aiSummary?.priorityAction || 'N/A'}"`],
        [],
        ['PERFORMANCE SUMMARY'],
        ['Total Target:', this.summary.totalTarget],
        ['Total Collected:', this.summary.totalCollected],
        ['Variance:', this.summary.variance],
        ['Performance:', `${this.summary.performance}%`],
        ['Total Transactions:', this.summary.totalTransactions],
        [],
        []
      ];

      const allRows = [...metadata, headers, ...rows];
      const csvContent = allRows.map(row => row.join(',')).join('\n');
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.href = url;
      link.download = `${this.departmentName.replace(/\s+/g, '_')}_Details_${moment().format('YYYYMMDD')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(url), 100);
    },

    // Utility Methods
    formatPrice(value) {
      if (!value && value !== 0) return 'KES 0';
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },

    formatDate(date) {
      if (!date) return '';
      return moment(date).format('DD MMM YYYY');
    },

    getPerformanceColor(value) {
      if (value >= 75) return 'text-success';
      if (value >= 50) return 'text-warning';
      return 'text-danger';
    },

    getPerformanceBg(value) {
      if (value >= 75) return 'bg-success';
      if (value >= 50) return 'bg-warning';
      return 'bg-danger';
    },

    getProgressBarClass(value) {
      if (value >= 75) return 'bg-success';
      if (value >= 50) return 'bg-warning';
      return 'bg-danger';
    },

    getStreamStatusBadgeClass(status) {
      const classes = {
        'EXCELLENT': 'bg-success text-white',
        'GOOD': 'bg-info text-white',
        'AVERAGE': 'bg-warning text-dark',
        'CRITICAL': 'bg-danger text-white'
      };
      return classes[status] || 'bg-secondary text-white';
    },

    getStreamRowClass(status) {
      if (status === 'CRITICAL') return 'table-danger';
      if (status === 'AVERAGE') return 'table-warning';
      return '';
    },

    getHeaderCardClass() {
      if (!this.aiSummary) return 'bg-primary';
      const status = this.aiSummary.overallStatus;
      if (status === 'EXCELLENT') return 'bg-success';
      if (status === 'GOOD') return 'bg-info';
      if (status === 'CRITICAL') return 'bg-danger';
      return 'bg-warning';
    },

    getIconColorClass() {
      if (!this.aiSummary) return 'text-primary';
      const status = this.aiSummary.overallStatus;
      if (status === 'EXCELLENT') return 'text-success';
      if (status === 'GOOD') return 'text-info';
      if (status === 'CRITICAL') return 'text-danger';
      return 'text-warning';
    },

    getStatusColor(status) {
      const colors = {
        'EXCELLENT': 'success',
        'GOOD': 'info',
        'CRITICAL': 'danger'
      };
      return colors[status] || 'warning';
    },

    getDepartmentIcon() {
      const deptName = this.departmentName.toLowerCase();
      const icons = {
        'trade': 'bx bx-store',
        'lands': 'bx bx-home',
        'agriculture': 'bx bx-leaf',
        'water': 'bx bx-droplet',
        'health': 'bx bx-plus-medical',
        'roads': 'bx bx-car',
        'education': 'bx bx-book',
        'tourism': 'bx bx-camera',
        'finance': 'bx bx-money',
        'environment': 'bx bx-world'
      };

      for (let key in icons) {
        if (deptName.includes(key)) {
          return icons[key];
        }
      }
      return 'bx bx-building';
    },

    gotTo(route) {
      router.push(route);
    },

    goBack() {
      router.push('department-performance');
    }
  }
}
</script>

<style scoped>
.bx-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.mini-stats-wid {
  border-radius: 0.5rem;
  transition: all 0.3s ease;
}

.mini-stats-wid:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.25rem 1rem rgba(0,0,0,0.1);
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

.table-danger {
  background-color: rgba(220, 53, 69, 0.1);
}

.table-warning {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-soft-success {
  background-color: rgba(25, 135, 84, 0.1);
}

.bg-soft-warning {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-soft-danger {
  background-color: rgba(220, 53, 69, 0.1);
}

.bg-soft-info {
  background-color: rgba(13, 202, 240, 0.1);
}

.bg-soft-secondary {
  background-color: rgba(108, 117, 125, 0.1);
}

.progress {
  background-color: #e9ecef;
}

.progress-bar {
  font-weight: 600;
}

.avatar-sm {
  height: 3rem;
  width: 3rem;
}

.avatar-lg {
  height: 5rem;
  width: 5rem;
}

.avatar-title {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.mini-stat-icon {
  width: 4rem;
  height: 4rem;
}

.card {
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075);
}

.badge {
  font-weight: 500;
  padding: 0.35rem 0.65rem;
}

.badge-lg {
  font-size: 1rem;
  padding: 0.5rem 1rem;
}

.alert-sm {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

canvas {
  max-height: 350px;
}
</style>